import { Textarea } from "@/components/ui/textarea";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import useSurvey from "../hooks/useSurvey";
import Field from "@/components/Field";
import InterQuestionToolbar from "./InterQuestionToolbar";
import QuestionCard from "./QuestionCard/QuestionCard";
import QuestionSwitch from "./QuestionSwitch";
import QuestionTypeSelect from "./QuestionTypeSelect";
import Fieldset from "@/components/Fieldset";
import { Hint } from "@/components/ui/hint";
import { isRequired } from "@/lib/form";
import question from "@/schemas/question";

interface QuestionSubFormProps {
  index: number;
  type: string;
  isFirst: boolean;
  isLast: boolean;
  disabled?: boolean;
  toolbar?: React.ReactNode;
  hint?: string;
}

export default memo(function QuestionSubForm({
  type,
  index,
  isFirst,
  isLast,
  toolbar,
  hint,
}: QuestionSubFormProps) {
  const { t } = useTranslation();

  const { isQuestionRemoved } = useSurvey();

  // Checking if question is removed because Virtuoso doesn't remove the item
  // from the list immediately after removing it, which causes the item to be
  // rendered and registered in the form context.
  if (isQuestionRemoved(index)) {
    return null;
  }

  return (
    <div data-testid={`question-${index}`} className="py-2">
      <QuestionCard
        index={index}
        className="grid grid-cols-2 gap-4"
        isFirst={isFirst}
        isLast={isLast}
        type={type}
      >
        <Fieldset className="grid grid-cols-1 gap-4 place-content-start">
          <Field
            name={`questions.${index}.type`}
            required={isRequired(question.shape.type)}
            label={t("surveyEditor.questionType")}
            render={({ field }) => (
              <QuestionTypeSelect index={index} {...field} />
            )}
          />
          <Field
            name={`questions.${index}.question`}
            required={isRequired(question.shape.question)}
            label={t("surveyEditor.questionDescription")}
            className="flex flex-col gap-2"
            render={({ field }) => <Textarea className="min-h-52" {...field} />}
          />
          {hint && <Hint>{hint}</Hint>}
        </Fieldset>
        <section className="grid grid-cols-1 gap-4 place-content-start">
          <QuestionSwitch index={index} />
        </section>
      </QuestionCard>
      <InterQuestionToolbar>{toolbar}</InterQuestionToolbar>
    </div>
  );
});
